import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { QUERY_KEYS, API_BASE_URL } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import type { RootStackParamList } from '../../navigation/types';

type ResultRoute = RouteProp<RootStackParamList, 'PaymentResult'>;

type PaymentResult = {
  status: 'completed' | 'failed' | 'timeout';
  payment_id: string;
  token_saved?: boolean;
};

type Props = {
  route: ResultRoute;
  navigation: any;
};

export default function PaymentResultScreen({ route, navigation }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();
  const { payment_id, plan_name, amount } = route.params;

  const [result, setResult] = useState<PaymentResult | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  // ─── SSE listener (XHR — works on React Native + Web) ───────────────
  useEffect(() => {
    const sseUrl = `${API_BASE_URL}/payments/onepay/status-stream/${payment_id}`;
    let cancelled = false;
    let processedLength = 0;

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.open('GET', sseUrl, true);
    xhr.setRequestHeader('Accept', 'text/event-stream');
    xhr.setRequestHeader('Cache-Control', 'no-cache');

    xhr.onprogress = () => {
      if (cancelled) return;

      // Only process new data since last onprogress
      const newText = xhr.responseText.substring(processedLength);
      processedLength = xhr.responseText.length;

      const lines = newText.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.status && data.payment_id) {
              setResult(data as PaymentResult);

              // Invalidate queries so Profile/history refreshes
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
              queryClient.invalidateQueries({ queryKey: ['savedCards'] });

              console.log('Payment status update received:', data.status);

              // Close the connection
              xhr.abort();
              return;
            }
          } catch {
            // ignore parse errors on keepalive comments etc.
          }
        }
      }
    };

    xhr.onerror = () => {
      if (cancelled) return;
      console.warn('SSE connection error');
    };

    xhr.send();

    return () => {
      cancelled = true;
      xhr.abort();
    };
  }, [payment_id, queryClient]);

  // ─── Timer for waiting state ───────────────────────────────────────────
  useEffect(() => {
    if (result) return;
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timer);
  }, [result]);

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleDone = () => {
    // Pop all the way back to main tabs
    navigation.reset({ index: 0, routes: [{ name: 'Main' }] });
  };

  const handleRetry = () => {
    navigation.goBack();
  };

  const isWaiting = !result;
  const isSuccess = result?.status === 'completed';
  const isFailed = result?.status === 'failed';
  const isTimeout = result?.status === 'timeout';

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg, paddingTop: insets.top }]}>
      <View style={styles.container}>
        {/* ─── Status Icon ─────────────────────────────────────── */}
        <View
          style={[
            styles.iconCircle,
            {
              backgroundColor: isWaiting
                ? 'rgba(255,255,255,0.05)'
                : isSuccess
                ? 'rgba(34,197,94,0.12)'
                : 'rgba(239,68,68,0.12)',
              borderColor: isWaiting
                ? colors.border
                : isSuccess
                ? colors.success
                : colors.error,
            },
          ]}
        >
          {isWaiting ? (
            <Icon name="clock-outline" size={48} color={colors.textSecondary} />
          ) : isSuccess ? (
            <Icon name="check-circle" size={48} color={colors.success} />
          ) : (
            <Icon name="close-circle" size={48} color={colors.error} />
          )}
        </View>

        {/* ─── Title / Message ─────────────────────────────────── */}
        {isWaiting && (
          <>
            <Text style={[styles.title, { color: colors.text }]}>{t('paymentResult.waitingTitle')}</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{t('paymentResult.waitingSubtitle')}</Text>
            <View style={[styles.timerBadge, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Icon name="timer-sand" size={14} color={colors.textTertiary} />
              <Text style={[styles.timerText, { color: colors.textTertiary }]}>
                {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
              </Text>
            </View>
          </>
        )}

        {isSuccess && (
          <>
            <Text style={[styles.title, { color: colors.text }]}>{t('paymentResult.successTitle')}</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{t('paymentResult.successSubtitle')}</Text>
            {result?.token_saved && (
              <View style={[styles.tokenBadge, { backgroundColor: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.3)' }]}>
                <Icon name="credit-card-check" size={14} color={colors.success} />
                <Text style={[styles.tokenBadgeText, { color: colors.success }]}>{t('paymentResult.cardSaved')}</Text>
              </View>
            )}
          </>
        )}

        {(isFailed || isTimeout) && (
          <>
            <Text style={[styles.title, { color: colors.text }]}>{isTimeout ? t('paymentResult.timeoutTitle') : t('paymentResult.failedTitle')}</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{isTimeout ? t('paymentResult.timeoutSubtitle') : t('paymentResult.failedSubtitle')}</Text>
          </>
        )}

        {/* ─── Order Summary ───────────────────────────────────── */}
        <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.summaryRow}>
            <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>{t('paymentResult.plan')}</Text>
            <Text style={[styles.summaryValue, { color: colors.text }]}>{plan_name}</Text>
          </View>
          <View style={[styles.summaryDivider, { borderBottomColor: colors.border }]} />
          <View style={styles.summaryRow}>
            <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>{t('paymentResult.amount')}</Text>
            <Text style={[styles.summaryValue, { color: colors.text }]}>
              {formatCurrency(parseFloat(amount))}
            </Text>
          </View>
          <View style={[styles.summaryDivider, { borderBottomColor: colors.border }]} />
          <View style={styles.summaryRow}>
            <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>{t('paymentResult.status')}</Text>
            <View style={styles.statusRow}>
              {isWaiting && (
                <>
                  <View style={[styles.statusDot, { backgroundColor: colors.warning }]} />
                  <Text style={[styles.summaryValue, { color: colors.warning }]}>{t('paymentResult.statusPending')}</Text>
                </>
              )}
              {isSuccess && (
                <>
                  <View style={[styles.statusDot, { backgroundColor: colors.success }]} />
                  <Text style={[styles.summaryValue, { color: colors.success }]}>{t('paymentResult.statusCompleted')}</Text>
                </>
              )}
              {(isFailed || isTimeout) && (
                <>
                  <View style={[styles.statusDot, { backgroundColor: colors.error }]} />
                  <Text style={[styles.summaryValue, { color: colors.error }]}>{isTimeout ? t('paymentResult.statusTimeout') : t('paymentResult.statusFailed')}</Text>
                </>
              )}
            </View>
          </View>
        </View>

        {/* ─── Action Buttons ──────────────────────────────────── */}
        <View style={styles.actions}>
          {isSuccess && (
            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
              activeOpacity={0.8}
              onPress={handleDone}
            >
              <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>{t('common.done')}</Text>
            </TouchableOpacity>
          )}

          {(isFailed || isTimeout) && (
            <>
              <TouchableOpacity
                style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
                activeOpacity={0.8}
                onPress={handleRetry}
              >
                <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>{t('paymentResult.tryAgain')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.secondaryBtn, { borderColor: colors.border }]}
                activeOpacity={0.7}
                onPress={handleDone}
              >
                <Text style={[styles.secondaryBtnText, { color: colors.textSecondary }]}>
                  {t('paymentResult.backToHome')}
                </Text>
              </TouchableOpacity>
            </>
          )}

          {isWaiting && (
            <TouchableOpacity
              style={[styles.secondaryBtn, { borderColor: colors.border }]}
              activeOpacity={0.7}
              onPress={handleDone}
            >
              <Text style={[styles.secondaryBtnText, { color: colors.textSecondary }]}>
                {t('paymentResult.cancelGoBack')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    ...responsiveContentStyle,
  },

  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    marginBottom: 24,
  },

  title: { fontSize: 24, fontWeight: '800', textAlign: 'center', marginBottom: 8 },
  subtitle: { fontSize: 14, textAlign: 'center', lineHeight: 22, marginBottom: 20, paddingHorizontal: 16 },

  timerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 24,
  },
  timerText: { fontSize: 13, fontWeight: '700', fontVariant: ['tabular-nums'] },

  tokenBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 24,
  },
  tokenBadgeText: { fontSize: 12, fontWeight: '700' },

  summaryCard: {
    width: '100%',
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    marginBottom: 32,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  summaryDivider: { borderBottomWidth: 1 },
  summaryLabel: { fontSize: 13 },
  summaryValue: { fontSize: 14, fontWeight: '700' },
  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  statusDot: { width: 8, height: 8, borderRadius: 4 },

  actions: { width: '100%', gap: 12 },
  primaryBtn: {
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
  },
  primaryBtnText: { fontSize: 16, fontWeight: '800' },
  secondaryBtn: {
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: 'center',
    borderWidth: 1,
  },
  secondaryBtnText: { fontSize: 14, fontWeight: '600' },
});
